import {
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";

import CoralDrawerContext from "@voxmedia/duet-context/context/coral-drawer-context";

interface Props {
  children?: ReactNode;
}

/**
 * Provider for Coral drawer state management.
 * Only rerenders components that need drawer state.
 */
const CoralDrawerProvider: FunctionComponent<PropsWithChildren<Props>> = ({
  children,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDrawerTransitionDisabled, setTransitionDisabled] = useState(false);
  const [isDrawerPrerendered, setIsDrawerPrerendered] = useState(false);

  const openDrawer = useCallback(
    (options: { disableTransition?: boolean } = {}) => {
      setTransitionDisabled(Boolean(options.disableTransition));
      setIsDrawerOpen(true);
    },
    [],
  );

  const closeDrawer = useCallback(
    (options: { disableTransition?: boolean } = {}) => {
      setTransitionDisabled(Boolean(options.disableTransition));
      setIsDrawerOpen(false);
      setIsDrawerPrerendered(false);
    },
    [],
  );

  const prerenderDrawer = useCallback(() => {
    setIsDrawerPrerendered(true);
  }, []);

  const drawerContextValue = useMemo(
    () => ({
      drawerId: "coral-drawer",
      isDrawerOpen,
      isDrawerTransitionDisabled,
      isDrawerPrerendered,
      openDrawer,
      closeDrawer,
      prerenderDrawer,
    }),
    [
      isDrawerOpen,
      isDrawerTransitionDisabled,
      isDrawerPrerendered,
      openDrawer,
      closeDrawer,
      prerenderDrawer,
    ],
  );

  return (
    <CoralDrawerContext.Provider value={drawerContextValue}>
      {children}
    </CoralDrawerContext.Provider>
  );
};

export default CoralDrawerProvider;
